import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Tooltip,
  TablePagination,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  FileCopy as DuplicateIcon,
  GetApp as ExportIcon,
  Visibility as ViewIcon,
} from '@material-ui/icons';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import {
  fetchRouteCardsStart,
  fetchDropdownsStart,
  deleteRouteCardStart,
  setSearchTerm,
  setClientFilter,
  setPartFilter,
  setStatusFilter,
  setPage,
  setPageSize,
  openStatusModal,
  closeStatusModal,
  approveRouteCardStart,
  rejectRouteCardStart,
  clearError,
} from '../../store/reducers/routeCardSlice';
import { RouteCard } from '../../services/routeCardApi';

/**
 * RouteCardList component that displays a list of route cards with filtering,
 * search, status management, and CRUD capabilities
 */
const RouteCardList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const state = useSelector((state: RootState) => state.routeCard);
  const routeCards = Array.isArray(state?.routeCards) ? state!.routeCards : [];
  const loading = !!state?.loading;
  const error = state?.error || null;
  const currentPage = state?.currentPage || 1;
  const pageSize = state?.pageSize || 10;
  const totalCount = state?.totalCount || 0;
  const searchTerm = state?.searchTerm || '';
  const clientFilter = state?.clientFilter || null;
  const partFilter = state?.partFilter || null;
  const statusFilter = state?.statusFilter || null;
  const clients = Array.isArray(state?.clients) ? state!.clients : [];
  const parts = Array.isArray(state?.parts) ? state!.parts : [];
  const routeCardStatuses = Array.isArray(state?.routeCardStatuses) ? state!.routeCardStatuses : [];
  const loadingDropdowns = !!state?.loadingDropdowns;
  const showStatusModal = !!state?.showStatusModal;
  const statusModalData = state?.statusModalData || null;

  // Local state
  const [selectedRouteCard, setSelectedRouteCard] = useState<RouteCard | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [statusComment, setStatusComment] = useState('');

  // Load data on component mount
  useEffect(() => {
    dispatch(fetchRouteCardsStart());
    dispatch(fetchDropdownsStart());
  }, [dispatch]);

  // Add debugging to see what data we're getting
  useEffect(() => {
    console.log('RouteCardList - Redux state:', {
      routeCards: routeCards,
      routeCardsLength: routeCards?.length,
      firstRouteCard: routeCards?.[0],
      loading,
      error
    });
    
    if (routeCards && routeCards.length > 0) {
      console.log('RouteCardList - First route card structure:', JSON.stringify(routeCards[0], null, 2));
      console.log('RouteCardList - First route card Part data:', routeCards[0].Part);
      console.log('RouteCardList - First route card Client data:', routeCards[0].Client);
      console.log('RouteCardList - First route card Status data:', routeCards[0].Status);
    }
    
    // Debug dropdown data
    console.log('RouteCardList - Dropdown data:', {
      clients: clients?.length ? clients : 'empty',
      parts: parts?.length ? parts : 'empty', 
      routeCardStatuses: routeCardStatuses?.length ? routeCardStatuses : 'empty'
    });
    
    // Debug dropdown arrays in detail
    console.log('RouteCardList - Detailed dropdown state:', {
      clientsArray: clients,
      clientsType: typeof clients,
      clientsIsArray: Array.isArray(clients),
      clientsLength: clients?.length,
      
      partsArray: parts,
      partsType: typeof parts,
      partsIsArray: Array.isArray(parts),
      partsLength: parts?.length,
      
      statusesArray: routeCardStatuses,
      statusesType: typeof routeCardStatuses,
      statusesIsArray: Array.isArray(routeCardStatuses),
      statusesLength: routeCardStatuses?.length,
      
      loadingDropdowns: loadingDropdowns
    });
  }, [routeCards, loading, error, clients, parts, routeCardStatuses]);

  // Helper functions to resolve IDs to names using dropdown data
  const getPartByPartId = (partId: number | string) => {
    if (!partId || !parts?.length) return null;
    return parts.find(part => ((part as any).Id || part.id) === String(partId));
  };

  const getClientByClientId = (clientId: number | string) => {
    if (!clientId || !clients?.length) return null;
    return clients.find(client => ((client as any).Id || client.id) === String(clientId));
  };

  const getStatusByStatusId = (statusId: number | string) => {
    if (!statusId || !routeCardStatuses?.length) return null;
    return routeCardStatuses.find(status => ((status as any).Id || status.id) === Number(statusId));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchTerm(event.target.value));
  };

  const handleClientFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    dispatch(setClientFilter(event.target.value as string | null));
  };

  const handlePartFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    dispatch(setPartFilter(event.target.value as string | null));
  };

  const handleStatusFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    dispatch(setStatusFilter(event.target.value as number | null));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1)); // Material-UI is 0-based, our API is 1-based
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setPageSize(parseInt(event.target.value, 10)));
  };

  const handleRefresh = () => {
    dispatch(fetchRouteCardsStart());
  };

  const handleCreateRouteCard = () => {
    navigate('/routecards/add');
  };

  const handleViewRouteCard = (routeCard: RouteCard) => {
    navigate(`/routecards/edit/${routeCard.id}`);
  };

  const handleEditRouteCard = (routeCard: RouteCard) => {
    navigate(`/routecards/edit/${routeCard.id}`);
  };

  const handleDeleteRouteCard = (routeCard: RouteCard) => {
    setSelectedRouteCard(routeCard);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedRouteCard) {
      dispatch(deleteRouteCardStart(selectedRouteCard.id!));
    }
    setDeleteConfirmOpen(false);
    setSelectedRouteCard(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setSelectedRouteCard(null);
  };

  const handleApproveRouteCard = (routeCard: RouteCard) => {
    dispatch(openStatusModal({ routeCardId: routeCard.id!, action: 'approve' }));
  };

  const handleRejectRouteCard = (routeCard: RouteCard) => {
    dispatch(openStatusModal({ routeCardId: routeCard.id!, action: 'reject' }));
  };

  const handleDuplicateRouteCard = (routeCard: RouteCard) => {
    // TODO: Implement duplicate functionality
    console.log('Duplicate route card:', routeCard.id);
  };

  const handleExportRouteCard = (routeCard: RouteCard) => {
    // TODO: Implement export functionality
    console.log('Export route card:', routeCard.id);
  };

  const handleStatusModalClose = () => {
    dispatch(closeStatusModal());
    setStatusComment('');
  };

  const handleStatusModalConfirm = () => {
    if (!statusModalData) return;

    if (statusModalData.action === 'approve') {
      dispatch(approveRouteCardStart({ id: statusModalData.routeCardId, comment: statusComment }));
    } else if (statusModalData.action === 'reject') {
      dispatch(rejectRouteCardStart({ id: statusModalData.routeCardId, comment: statusComment }));
    }

    handleStatusModalClose();
  };

  const handleErrorClose = () => {
    dispatch(clearError());
  };

  const getStatusColor = (statusId: number): 'default' | 'primary' | 'secondary' => {
    switch (statusId) {
      case 1: return 'secondary'; // Pending
      case 2: return 'primary';   // Approved
      case 3: return 'default';   // Rejected
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loadingDropdowns) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" style={{ marginLeft: '1rem' }}>
          Loading Route Cards...
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Route Cards
        </Typography>
        <Box display="flex" style={{ gap: '8px' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateRouteCard}
          >
            Add Route Card Template
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper style={{ padding: '1rem', marginBottom: '1rem' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search Route Cards"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: <SearchIcon color="action" />,
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Client</InputLabel>
              <Select
                value={clientFilter || ''}
                onChange={handleClientFilterChange}
                label="Client"
              >
                <MenuItem value="">All Clients</MenuItem>
                {clients.map((client) => (
                  <MenuItem key={(client as any).Id || client.id} value={(client as any).Id || client.id}>
                    {client.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Part</InputLabel>
              <Select
                value={partFilter || ''}
                onChange={handlePartFilterChange}
                label="Part"
                disabled={!clientFilter}
              >
                <MenuItem value="">All Parts</MenuItem>
                {(() => {
                  console.log('Parts dropdown filter debug:', {
                    clientFilter,
                    clientFilterType: typeof clientFilter,
                    totalParts: parts.length,
                    firstPart: parts[0],
                    firstPartClientId: parts[0] ? ((parts[0] as any).ClientId || (parts[0] as any).client_id) : 'no parts'
                  });
                  
                  const filteredParts = parts.filter(part => {
                    if (!clientFilter) return true; // Show all if no client selected
                    
                    const partClientId = (part as any).ClientId || (part as any).client_id;
                    const match = String(partClientId) === String(clientFilter);
                    
                    console.log('Part filter check:', {
                      partId: (part as any).Id,
                      partNumber: part.Number,
                      partClientId,
                      clientFilter,
                      match
                    });
                    
                    return match;
                  });
                  
                  console.log('Filtered parts result:', {
                    filteredCount: filteredParts.length,
                    filteredParts: filteredParts.map(p => ({ id: (p as any).Id, number: p.Number, clientId: (p as any).ClientId }))
                  });
                  
                  return filteredParts.map((part) => (
                    <MenuItem key={(part as any).Id || part.id} value={(part as any).Id || part.id}>
                      {part.Number} - {part.Description}
                    </MenuItem>
                  ));
                })()}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter || ''}
                onChange={handleStatusFilterChange}
                label="Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                {routeCardStatuses.map((status) => (
                  <MenuItem key={(status as any).Id || status.id} value={(status as any).Id || status.id}>
                    {status.StatusName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleErrorClose}
        message={error}
      />

      {/* Route Cards Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Route Card Name</TableCell>
              <TableCell>Part Number</TableCell>
              <TableCell>Issue</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Template Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={30} />
                  <Typography variant="body2" style={{ marginLeft: '1rem' }}>
                    Loading route cards...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : routeCards.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No route cards found. {searchTerm && `Try adjusting your search term.`}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              routeCards.map((routeCard) => {
                // Resolve related data using dropdown arrays
                const part = getPartByPartId(routeCard.PartId);
                const client = getClientByClientId(routeCard.ClientId);
                const status = getStatusByStatusId(routeCard.RCTStatusId);
                
                return (
                <TableRow key={routeCard.id} hover>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                      {routeCard.Name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {routeCard.Description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {part?.Number || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {part?.Description}
                    </Typography>
                  </TableCell>
                  <TableCell>{routeCard.Issue}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {client?.Name || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={status?.StatusName || 'Unknown'}
                      color={
                        status?.StatusName === 'Approved'
                          ? 'primary'
                          : status?.StatusName === 'Rejected'
                          ? 'secondary'
                          : 'default'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" style={{ gap: '4px' }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewRouteCard(routeCard)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEditRouteCard(routeCard)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteRouteCard(routeCard)}
                          color="secondary"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Change Status">
                        <IconButton
                          size="small"
                          onClick={() => handleApproveRouteCard(routeCard)}
                          style={{ color: 'green' }}
                        >
                          <ApproveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Duplicate">
                        <IconButton
                          size="small"
                          onClick={() => handleDuplicateRouteCard(routeCard)}
                        >
                          <DuplicateIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Export">
                        <IconButton
                          size="small"
                          onClick={() => handleExportRouteCard(routeCard)}
                        >
                          <ExportIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={currentPage - 1} // Material-UI is 0-based, our API is 1-based
          onPageChange={handlePageChange}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handlePageSizeChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Route cards per page:"
        />
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete Route Card</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the route card "{selectedRouteCard?.Name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="secondary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Change Modal */}
      <Dialog open={showStatusModal} onClose={handleStatusModalClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {statusModalData?.action === 'approve' ? 'Approve' : 'Reject'} Route Card Template
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Please provide comments for this status change:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            placeholder="Write some comments..."
            value={statusComment}
            onChange={(e) => setStatusComment(e.target.value)}
            style={{ marginTop: '1rem' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStatusModalClose}>Cancel</Button>
          <Button
            onClick={handleStatusModalConfirm}
            color={statusModalData?.action === 'approve' ? 'primary' : 'secondary'}
            variant="contained"
          >
            {statusModalData?.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RouteCardList; 